/**
 * Watchdog no painel: abre a sessão do computador, acompanha a vez e os pedidos de acesso
 * (decisões 189 a 191 e 245).
 *
 * - Na abertura: `POST watchdog/session`. Primeiro computador entra direto; computador novo
 *   abre um pedido e fica esperando a autorização (consulta a cada 4 s); com outro computador em
 *   uso, pergunta antes de pegar a vez. Da segunda troca do dia em diante, a pausa traz a espera
 *   (`wait`): este computador só pega a vez a partir de `availableAt`.
 * - Com a vez: confere a cada minuto e ao voltar para a aba. Se outro computador pegou a vez, a
 *   captura em andamento pausa (guarda o que já veio) e a janela "Você abriu em outro computador"
 *   aparece por cima, sem trocar de tela.
 * - Uma consulta por navegador (25/10/2026, pedido do Marcelo): as abas combinam pelo horário da
 *   última consulta, guardado no localStorage, e a que consultou passa o resultado às outras
 *   (BroadcastChannel). Aba escondida não consulta, a não ser com captura rodando nela (que precisa
 *   pausar se outro computador pegar a vez). Voltar para a aba dispara focus e visibilitychange
 *   juntos: vira uma consulta só.
 * - Rota fora do ar ou falha de rede na abertura: segue sem Watchdog (fase `off`).
 */
import { useCallback, useEffect } from 'react';
import {
  claimTurn,
  decideRequest,
  fetchDevices,
  fetchStatus,
  openSession,
  pollRequest,
  removeDevice,
  requestAgain,
  type WatchdogSession,
  type WatchdogStatus,
} from '../../api/watchdog';
import { clearWatchdogToken, getWatchdogToken, setWatchdogToken } from '../../lib/watchdog';
import { readAuth } from '../../lib/auth';
import { useWatchdogStore, waitOf } from '../../stores/watchdogStore';
import { useCaptureStore } from '../../stores/captureStore';
import { pauseCaptureRuntime } from '../../lib/captureRuntime';
import { stopCapture } from '../../lib/extensionBridge';

const STATUS_MS = 60 * 1000;
const POLL_MS = 4 * 1000;
/** Intervalo entre consultas no navegador inteiro (um pouco abaixo do minuto, pela folga do relógio). */
const STATUS_GAP_MS = 50 * 1000;
/** Voltar para a aba: consulta, a menos que alguma aba tenha consultado há tão pouco. */
const RETURN_GAP_MS = 5 * 1000;
const STATUS_AT_KEY = 'lm.watchdog.statusAt';
const LOCK_NAME = 'lm-watchdog-status';
const CHANNEL_NAME = 'lm-watchdog';

type SharedStatus = Extract<WatchdogStatus, { state: 'off' | 'active' | 'paused' }>;

let opening: Promise<void> | null = null;

function userId(): string | undefined {
  return readAuth()?.userId;
}

function keepToken(session: WatchdogSession): void {
  const id = userId();
  // Em pausa também: o primeiro computador pode abrir com a troca em espera.
  if (id && (session.state === 'active' || session.state === 'paused') && session.token) {
    setWatchdogToken(id, session.token);
  }
}

/** Abre (ou reabre) a sessão do computador. Uma só por vez, mesmo com várias chamadas. */
export function openWatchdog(): Promise<void> {
  if (opening) return opening;
  const store = useWatchdogStore.getState();
  if (store.phase === 'idle') store.set({ phase: 'checking' });
  opening = openSession()
    .then((result) => {
      if (!result.ok) {
        // Sem a rota no ar, sem rede ou recusado por outro motivo: o painel segue sem Watchdog.
        useWatchdogStore.getState().apply({ state: 'off' });
        return;
      }
      keepToken(result.data);
      useWatchdogStore.getState().apply(result.data);
    })
    .finally(() => {
      opening = null;
    });
  return opening;
}

/** Para a captura deste computador quando outro pega a vez (o que já veio fica guardado). */
function pauseLocalCapture(): void {
  const capture = useCaptureStore.getState();
  if (capture.activeListId) void pauseCaptureRuntime(capture.activeListId, 'user_requested');
  else if (capture.active) stopCapture();
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function lastStatusAt(): number {
  const value = Number(storage()?.getItem(STATUS_AT_KEY) ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function markStatusAt(at: number): void {
  try {
    storage()?.setItem(STATUS_AT_KEY, String(at));
  } catch {
    // Sem storage: cada aba consulta por si, como antes.
  }
}

let channel: BroadcastChannel | null | undefined;

/** Canal entre as abas deste navegador (sem BroadcastChannel, cada aba fica por si). */
function statusChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  // No Node (testes), o canal aberto não segura o processo; no navegador, unref não existe.
  (channel as unknown as { unref?: () => void } | null)?.unref?.();
  return channel;
}

/** Captura rodando nesta aba: ela precisa saber logo se outro computador pegou a vez. */
function captureRunning(): boolean {
  const capture = useCaptureStore.getState();
  return Boolean(capture.activeListId || capture.active);
}

/** Aba visível, ou escondida com captura rodando. */
export function shouldCheckStatus(): boolean {
  return document.visibilityState === 'visible' || captureRunning();
}

/** Aplica o estado que esta aba consultou ou que outra aba passou. */
function applyStatus(data: SharedStatus): void {
  const store = useWatchdogStore.getState();
  if (data.state === 'off') {
    store.apply({ state: 'off' });
  } else if (data.state === 'paused') {
    if (store.phase === 'active') pauseLocalCapture();
    store.set({
      phase: 'paused',
      activeDevice: data.activeDevice,
      wait: waitOf(data),
      pausedWhileUsing: store.phase === 'active' || store.pausedWhileUsing,
    });
  } else if (data.state === 'active') {
    store.set({ phase: 'active', pending: data.pending ?? [], wait: null, pausedWhileUsing: false });
  }
}

export async function checkWatchdogStatus(): Promise<void> {
  const result = await fetchStatus();
  if (!result.ok) return;
  const data = result.data;
  if (data.state === 'device_required') {
    // Só esta aba reabre a sessão; as outras descobrem na consulta delas.
    const id = userId();
    if (id) clearWatchdogToken(id);
    await openWatchdog();
    return;
  }
  statusChannel()?.postMessage({ type: 'status', data });
  applyStatus(data);
}

/**
 * Consulta uma vez pelo navegador inteiro: se alguma aba consultou há menos de `gapMs`, não
 * consulta. Com Web Locks, a conferência e a marca acontecem numa aba por vez.
 */
export async function checkWatchdogStatusShared(gapMs: number): Promise<void> {
  const run = async (): Promise<void> => {
    const now = Date.now();
    if (now - lastStatusAt() < gapMs) return;
    markStatusAt(now);
    await checkWatchdogStatus();
  };
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) await locks.request(LOCK_NAME, run);
  else await run();
}

export async function checkWatchdogRequest(): Promise<void> {
  const store = useWatchdogStore.getState();
  const request = store.request;
  if (!request) return;
  const result = await pollRequest(request.id);
  if (!result.ok) {
    if (result.code === 'not_found') await openWatchdog();
    return;
  }
  const data = result.data;
  if (data.status === 'approved') {
    const id = userId();
    if (id && 'token' in data && data.token) setWatchdogToken(id, data.token);
    // Sem token: outra aba deste computador recebeu e guardou; a sessão abre com ele.
    if (!id || getWatchdogToken(id)) await openWatchdog();
  } else if (data.status === 'refused') {
    store.set({ phase: 'refused' });
  } else if (data.status === 'expired') {
    store.set({ phase: 'expired' });
  } else if (data.status === 'off') {
    store.apply({ state: 'off' });
  }
}

/**
 * Liga o Watchdog quando a conta está pronta (`/app/me` respondeu ativo) e mantém o estado em dia.
 * Fica montado no AppShell.
 */
export function useWatchdogBoot(ready: boolean): void {
  const phase = useWatchdogStore((s) => s.phase);

  useEffect(() => {
    if (ready && useWatchdogStore.getState().phase === 'idle') void openWatchdog();
  }, [ready]);

  // Com a vez (ou em pausa durante o uso): confere a cada minuto, uma vez pelo navegador inteiro,
  // e ao voltar para a aba. Aba escondida sem captura não confere.
  useEffect(() => {
    if (phase !== 'active' && phase !== 'paused') return undefined;
    const tick = (): void => {
      if (shouldCheckStatus()) void checkWatchdogStatusShared(STATUS_GAP_MS);
    };
    const onReturn = (): void => {
      if (document.visibilityState === 'visible') void checkWatchdogStatusShared(RETURN_GAP_MS);
    };
    const id = window.setInterval(tick, STATUS_MS);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [phase]);

  // O que outra aba consultou vale aqui também (pedidos de acesso, vez com outro computador).
  useEffect(() => {
    const bus = statusChannel();
    if (!bus) return undefined;
    const onMessage = (event: MessageEvent<{ type?: string; data?: SharedStatus }>): void => {
      if (event.data?.type !== 'status' || !event.data.data) return;
      const current = useWatchdogStore.getState().phase;
      if (current === 'active' || current === 'paused') applyStatus(event.data.data);
    };
    bus.addEventListener('message', onMessage);
    return () => bus.removeEventListener('message', onMessage);
  }, []);

  // Pedido aberto: consulta a cada 4 s até a autorização chegar.
  useEffect(() => {
    if (phase !== 'authorize') return undefined;
    const id = window.setInterval(() => void checkWatchdogRequest(), POLL_MS);
    return () => window.clearInterval(id);
  }, [phase]);
}

/** Ações das telas do Watchdog. Cada uma devolve o código do erro, ou null quando deu certo. */
export function useWatchdogActions() {
  const claim = useCallback(async (): Promise<string | null> => {
    const result = await claimTurn();
    if (!result.ok) return result.code;
    useWatchdogStore.getState().apply(result.data);
    return null;
  }, []);

  const askAgain = useCallback(async (): Promise<string | null> => {
    const result = await requestAgain();
    if (!result.ok) return result.code;
    keepToken(result.data);
    useWatchdogStore.getState().apply(result.data);
    return null;
  }, []);

  const refreshDevices = useCallback(async (): Promise<string | null> => {
    const result = await fetchDevices();
    if (!result.ok) return result.code;
    useWatchdogStore.getState().set({ devices: result.data.devices, pending: result.data.pending });
    return null;
  }, []);

  const decide = useCallback(
    async (id: string, decision: 'approve' | 'refuse'): Promise<string | null> => {
      const result = await decideRequest(id, decision);
      if (!result.ok) {
        await refreshDevices();
        return result.code;
      }
      useWatchdogStore.getState().set({ devices: result.data.devices, pending: result.data.pending });
      return null;
    },
    [refreshDevices],
  );

  const remove = useCallback(async (id: string): Promise<string | null> => {
    const result = await removeDevice(id);
    if (!result.ok) return result.code;
    useWatchdogStore.getState().set({ devices: result.data.devices, pending: result.data.pending });
    return null;
  }, []);

  return { claim, askAgain, refreshDevices, decide, remove };
}
